<template>
    <div class="relative">
        <div class="vx-navbar-wrapper">
            <vs-navbar class="vx-navbar navbar-custom" :color="navbarColor" :class="classObj">

                <!-- Mobile sidebar toggle -->
                <feather-icon
                    class="sm:inline-flex xl:hidden cursor-pointer mr-3"
                    icon="MenuIcon"
                    @click.stop="showSidebar"
                ></feather-icon>

                <template v-if="breakpoint != 'md'">
                    <!-- Starred pages -->
                    <ul class="vx-navbar__starred-pages">
                        <draggable v-model="starredPagesLimited" :group="{name: 'pinList'}" class="flex cursor-move">
                            <li class="starred-page" v-for="page in starredPagesLimited" :key="page.url">
                                <vx-tooltip :text="page.label" position="bottom" delay=".3s">
                                    <feather-icon svgClasses="h-6 w-6" class="p-2 cursor-pointer" :icon="page.labelIcon" @click="$router.push(page.url)"></feather-icon>
                                </vx-tooltip>
                            </li>
                        </draggable>
                    </ul>
                    <div class="vx-navbar__starred-pages--more-dropdown" v-if="starredPagesMore.length">
                        <vs-dropdown vs-custom-content vs-trigger-click>
                            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" class="cursor-pointer p-2"></feather-icon>
                            <vs-dropdown-menu>
                                <ul class="vx-navbar__starred-pages-more--list">
                                    <draggable v-model="starredPagesMore" :group="{name: 'pinList'}" class="cursor-move">
                                        <li class="starred-page--more flex items-center cursor-pointer" v-for="page in starredPagesMore" :key="page.url" @click="$router.push(page.url)">
                                            <feather-icon svgClasses="h-5 w-5" class="ml-2 mr-1" :icon="page.labelIcon"></feather-icon>
                                            <span class="px-2 pt-2 pb-1">{{ page.label }}</span>
                                        </li>
                                    </draggable>
                                </ul>
                            </vs-dropdown-menu>
                        </vs-dropdown>
                    </div>
                    <!-- Breadcrumb -->
                    <vx-breadcrumb class="ml-4 md:flex hidden" v-if="$route.meta.breadcrumb" />
                </template>

                <vs-spacer></vs-spacer>

                <!-- ─── Right section ──────────────────────────────────────────── -->
                <div class="navbar-right-section">

                    <!-- Image resizer tool -->
                    <a href="https://imageresizer.com/fr" target="_blank" class="navbar-icon-btn" title="Redimensionner une image">
                        <feather-icon icon="CameraIcon" svgClasses="w-4 h-4"></feather-icon>
                    </a>

                    <!-- Divider -->
                    <div class="navbar-divider"></div>

                    <!-- User dropdown -->
                    <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
                        <div class="user-chip">
                            <div class="user-avatar-initials">
                                {{ userInitials }}
                            </div>
                            <div class="user-text hidden sm:block">
                                <p class="user-name">{{ userFullname }}</p>
                                <span class="user-email">{{ userEmail }}</span>
                            </div>
                            <feather-icon icon="ChevronDownIcon" svgClasses="w-3.5 h-3.5" class="user-chevron hidden sm:block"></feather-icon>
                        </div>

                        <vs-dropdown-menu class="vx-navbar-dropdown user-dropdown-menu">
                            <div class="dropdown-header">
                                <div class="dropdown-avatar">{{ userInitials }}</div>
                                <div>
                                    <p class="dropdown-name">{{ userFullname }}</p>
                                    <span class="dropdown-email">{{ userEmail }}</span>
                                </div>
                            </div>
                            <div class="dropdown-divider"></div>
                            <ul>
                                <li class="dropdown-item" @click="$router.push({name: 'profile'})">
                                    <feather-icon icon="UserIcon" svgClasses="w-4 h-4"></feather-icon>
                                    <span>Mon Profil</span>
                                </li>
                                <li class="dropdown-item danger" @click="logout">
                                    <feather-icon icon="LogOutIcon" svgClasses="w-4 h-4"></feather-icon>
                                    <span>Se déconnecter</span>
                                </li>
                            </ul>
                        </vs-dropdown-menu>
                    </vs-dropdown>
                </div>

            </vs-navbar>
        </div>
    </div>
</template>

<script>
import VxAutoSuggest from '@/components/vx-auto-suggest/VxAutoSuggest.vue';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import draggable from 'vuedraggable';
import AuthModule from '../../store/AuthModule';

export default {
    name: 'the-navbar',
    props: {
        navbarColor: {
            type: String,
            default: '#fff',
        },
    },
    data() {
        return {
            navbarSearchAndPinList: this.$store.state.navbarSearchAndPinList,
            searchQuery: '',
            showFullSearch: false,
            showBookmarkPagesDropdown: false,
        };
    },
    watch: {
        $route() {
            if (this.showBookmarkPagesDropdown) this.showBookmarkPagesDropdown = false;
        },
    },
    computed: {
        sidebarWidth() {
            return this.$store.state.sidebarWidth;
        },
        breakpoint() {
            return this.$store.state.breakpoint;
        },
        classObj() {
            if (this.sidebarWidth == 'default') return 'navbar-default';
            else if (this.sidebarWidth == 'reduced') return 'navbar-reduced';
            else if (this.sidebarWidth) return 'navbar-full';
        },
        starredPages() {
            return this.$store.state.starredPages;
        },
        starredPagesLimited: {
            get() { return this.starredPages.slice(0, 10); },
            set(list) { this.$store.dispatch('arrangeStarredPagesLimited', list); },
        },
        starredPagesMore: {
            get() { return this.starredPages.slice(10); },
            set(list) { this.$store.dispatch('arrangeStarredPagesMore', list); },
        },
        userFullname() {
            return `${AuthModule.firstname} ${AuthModule.lastname}`;
        },
        userEmail() {
            return `${AuthModule.email}`;
        },
        userInitials() {
            const f = (AuthModule.firstname || '').charAt(0).toUpperCase();
            const l = (AuthModule.lastname || '').charAt(0).toUpperCase();
            return (f + l) || '?';
        },
    },
    methods: {
        async logout() {
            await AuthModule.logout();
            this.$router.push({ name: 'login' });
        },
        showSidebar() {
            this.$store.commit('TOGGLE_IS_SIDEBAR_ACTIVE', true);
        },
    },
    components: {
        VxAutoSuggest,
        VuePerfectScrollbar,
        draggable,
    },
};
</script>

<style lang="scss" scoped>
// ─── Right section ────────────────────────────────────────────────────────────
.navbar-right-section {
    display: flex;
    align-items: center;
    gap: 6px;
}

// Camera / icon button
.navbar-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: transparent;
    color: #78909c;
    text-decoration: none;
    transition: background 0.18s ease, color 0.18s ease;

    &:hover {
        background: rgba(var(--vs-primary), 0.08);
        color: rgba(var(--vs-primary), 1);
    }
}

// Divider
.navbar-divider {
    width: 1px;
    height: 24px;
    background: #edf2f7;
    margin: 0 4px;
}

// User chip (trigger)
.user-chip {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 10px 5px 5px;
    border-radius: 12px;
    border: 1.5px solid #edf2f7;
    background: #fff;
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
    cursor: pointer;

    &:hover {
        border-color: rgba(var(--vs-primary), 0.3);
        box-shadow: 0 2px 8px rgba(var(--vs-primary), 0.1);
    }

    .user-avatar-initials {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        background: linear-gradient(135deg, rgba(var(--vs-primary), 1), rgba(var(--vs-primary), 0.7));
        color: #fff;
        font-size: 11px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        letter-spacing: 0.5px;
        flex-shrink: 0;
    }

    .user-text {
        line-height: 1.2;

        .user-name {
            font-size: 13px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
            white-space: nowrap;
        }

        .user-email {
            font-size: 11px;
            color: #90a4ae;
            white-space: nowrap;
        }
    }

    .user-chevron {
        color: #b0bec5;
        flex-shrink: 0;
    }
}

// Dropdown menu
.user-dropdown-menu /deep/ .vs-dropdown--menu {
    padding: 0;
    border-radius: 14px;
    border: 1.5px solid #edf2f7;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    min-width: 220px;
}

.dropdown-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: #f8faff;

    .dropdown-avatar {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: linear-gradient(135deg, rgba(var(--vs-primary), 1), rgba(var(--vs-primary), 0.7));
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .dropdown-name {
        font-size: 13.5px;
        font-weight: 600;
        color: #2d3748;
        margin: 0 0 2px;
    }

    .dropdown-email {
        font-size: 11.5px;
        color: #90a4ae;
    }
}

.dropdown-divider {
    height: 1px;
    background: #edf2f7;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 16px;
    font-size: 13.5px;
    font-weight: 500;
    color: #4a5568;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    list-style: none;

    &:hover {
        background: rgba(var(--vs-primary), 0.06);
        color: rgba(var(--vs-primary), 1);
    }

    &.danger:hover {
        background: rgba(234, 84, 85, 0.06);
        color: #ea5455;
    }
}
</style>
